import React, { useEffect, useRef } from "react";
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import moment from "moment";

// Register required chart components
Chart.register(
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Filler
);

const LineChart = ({ chartData, title = "Uploads Per Day" }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!chartData || !chartData.length) return;

    // Sort chartData by date chronologically
    const sortedData = [...chartData].sort((a, b) => new Date(a._id) - new Date(b._id));

    const labels = sortedData.map((item) => moment(item._id).format("ddd").toUpperCase());
    const dates = sortedData.map((item) => moment(item._id).format("YYYY-MM-DD"));
    const dataCounts = sortedData.map((item) => item.count);

    // Destroy old chart to avoid duplication
    if (chartRef.current) {
      chartRef.current.destroy();
    }

    chartRef.current = new Chart(canvasRef.current, {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            label: title,
            data: dataCounts,
            fill: false,
            borderColor: "#8b5cf6", // Purple/Indigo line
            borderWidth: 3,
            tension: 0.4, // Smooth curve
            pointBackgroundColor: "#ffffff",
            pointBorderColor: "#8b5cf6",
            pointBorderWidth: 3,
            pointRadius: 6,
            pointHoverRadius: 8,
            pointHoverBackgroundColor: "#8b5cf6",
            pointHoverBorderColor: "#ffffff",
            pointHoverBorderWidth: 3,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            enabled: true,
            backgroundColor: "#ffffff",
            titleColor: "#111827",
            titleFont: {
              family: "Inter, sans-serif",
              size: 12,
              weight: "bold",
            },
            bodyColor: "#4b5563",
            bodyFont: {
              family: "Inter, sans-serif",
              size: 12,
            },
            borderColor: "rgba(0, 0, 0, 0.08)",
            borderWidth: 1,
            padding: 12,
            cornerRadius: 8,
            displayColors: true,
            boxPadding: 6,
            usePointStyle: true,
            callbacks: {
              title: (context) => {
                const index = context[0].dataIndex;
                const day = labels[index];
                const date = dates[index];
                return `${day} · ${date}`;
              },
              label: (context) => {
                return ` Uploads: ${context.raw}`;
              },
            },
          },
        },
        scales: {
          x: {
            grid: {
              display: false,
              drawBorder: false,
            },
            ticks: {
              color: "#9ca3af",
              font: {
                family: "Inter, sans-serif",
                size: 11,
                weight: "600",
              },
            },
          },
          y: {
            grid: {
              borderDash: [5, 5],
              color: "rgba(0, 0, 0, 0.05)",
              drawBorder: false,
            },
            ticks: {
              color: "#9ca3af",
              precision: 0,
              font: {
                family: "Inter, sans-serif",
                size: 11,
                weight: "500",
              },
            },
          },
        },
      },
    });

    return () => {
      chartRef.current?.destroy();
    };
  }, [chartData, title]);

  return (
    <div style={{ height: "350px", position: "relative" }}>
      <canvas ref={canvasRef}></canvas>
    </div>
  );
};

export default LineChart;
